import {useCallback, useEffect, useState} from "react";

import {SchedulePolicy, useTimeout} from "@khanacademy/wonder-blocks-timing";
import type {ActionBubble} from "@khan/guide-types";

interface Result {
    displayedActions: ReadonlyArray<ActionBubble>;
    allActionsDisplayed: boolean;
}

/**
 * A custom hook that manages the deference of action bubbles to be displayed.
 */
export const useDeferDisplayedActions = (
    actions: ReadonlyArray<ActionBubble>,
    delayMs: number,
): Result => {
    const [displayedActions, setDisplayedActions] = useState(
        delayMs <= 0 ? actions : [],
    );

    // Create a timer that will reveal another action bubble after 1 second
    const timeout = useTimeout(
        useCallback(() => {
            // If we've already displayed all the action bubbles, we don't need
            // to do anything further.
            if (displayedActions.length === actions.length) {
                return;
            }
            // We want to reveal the actions in reverse order
            const updatedDisplayedActions = actions.slice(
                actions.length - (displayedActions.length + 1),
                actions.length,
            );
            setDisplayedActions(updatedDisplayedActions);
        }, [actions, displayedActions]),
        delayMs,
        {
            schedulePolicy: SchedulePolicy.OnDemand,
        },
    );

    // Handles when the notification updates (when the actions change). If the
    // user prefers reduced motion, we display the updated actions immediately,
    // otherwise trigger the timeout loop to reveal the action bubbles one at
    // a time.
    useEffect(() => {
        // We handle two types of updates to actions:
        // 1. Addition of actions – trigger timeout loop to reveal actions
        // 2. Removal of actions - immediately update actions
        if (actions.length > displayedActions.length && delayMs > 0) {
            timeout.set();
        } else {
            setDisplayedActions(actions);
        }
        // We only want to trigger this when notificationId changes or there
        // has been an update to actions.
        // eslint-disable-next-line react-hooks/exhaustive-deps -- porting from old code.
    }, [actions]);

    // Creates and maintains a timeout loop (updates the displayed action
    // bubbles which triggers this effect again) until all the action bubbles
    // are displayed. This is a no-op if the delayMs is 0.
    useEffect(() => {
        if (delayMs <= 0) {
            return;
        }
        timeout.set();
        // only want to re-trigger this when displayedActions change
        // eslint-disable-next-line react-hooks/exhaustive-deps -- porting from old code.
    }, [displayedActions]);

    return {
        displayedActions,
        allActionsDisplayed: actions.length === displayedActions.length,
    };
};
